import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { EMPTY, switchMap } from 'rxjs';
import { ApplicationStore, ApplicationStoreType } from '../../core/store/application.store';
import { ButtonComponent, ButtonType } from '../../shared/components/button/button.component';
import {
    NOT_SELECTABLE_KEY,
    OptionToggleComponent,
} from '../../shared/components/option-toggle/option-toggle.component';
import { Option } from '../../shared/types/option';
import { Department, DEPARTMENT_OPTIONS } from '../../shared/types/department';
import { ClientLoggingService } from '../../core/services/client-logging.service';
import { generateErrorId } from '../../shared/utils/unique-id-utils';
import { FORBIDDEN_EMAIL_DOMAINS } from '../../shared/utils/forbidden-domains';
import { RegistrationService } from '../../core/services/registration.service';
import { OnboardingService } from '../../onboarding/onboarding.service';
import { PASSWORD_MIN_LENGTH, passwordStrengthValidator } from '../../shared/utils/password-validators';

@Component({
    selector: 'juyo-sign-up',
    styleUrl: 'sign-up.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, ButtonComponent, OptionToggleComponent],
    template: `
        @if (step() === 'details') {
            <div class="heading-section">
                <h1 class="heading">Get started</h1>
                <p class="subtitle">Create your account to start using Juyo.</p>
            </div>

            @if (apiError()) {
                <div class="error-banner">{{ apiError() }}</div>
            }

            <form class="form" [formGroup]="form" novalidate>
                <div class="name-row">
                    <div class="field-group">
                        <label class="field-label" for="firstName"> FIRST NAME <span class="required">*</span> </label>
                        <input
                            type="text"
                            id="firstName"
                            class="field"
                            [class.invalid]="form.controls.firstName.invalid && form.controls.firstName.touched"
                            placeholder="James"
                            formControlName="firstName"
                        />
                    </div>
                    <div class="field-group">
                        <label class="field-label" for="lastName"> LAST NAME <span class="required">*</span> </label>
                        <input
                            type="text"
                            id="lastName"
                            class="field"
                            [class.invalid]="form.controls.lastName.invalid && form.controls.lastName.touched"
                            placeholder="Jones"
                            formControlName="lastName"
                        />
                    </div>
                </div>

                <label class="field-label" for="email"> WORK EMAIL <span class="required">*</span> </label>
                <input
                    type="email"
                    id="email"
                    class="field"
                    [class.invalid]="form.controls.email.invalid && form.controls.email.touched"
                    placeholder="you@organization.com"
                    formControlName="email"
                />
                @if (form.controls.email.invalid && form.controls.email.touched) {
                    @if (form.controls.email.errors?.['forbiddenDomain']) {
                        <div class="field-error field-error--work-email">
                            <span class="material-symbols-outlined field-error-icon">error</span>
                            <span
                                >Please use your <strong>work email</strong> address (e.g. you&#64;organization.com).
                                Personal email providers like Gmail, Yahoo, and Outlook are not accepted.</span
                            >
                        </div>
                    } @else {
                        <p class="field-error">Please enter a valid email address (e.g. you&#64;organization.com)</p>
                    }
                }

                <label class="field-label" for="password"> PASSWORD <span class="required">*</span> </label>
                <div class="password-wrapper">
                    <input
                        [type]="showPassword() ? 'text' : 'password'"
                        id="password"
                        class="field"
                        [class.invalid]="form.controls.password.invalid && form.controls.password.touched"
                        placeholder="Min {{ passwordMinLength }} characters"
                        formControlName="password"
                    />
                    <button type="button" class="eye-toggle" (click)="showPassword.set(!showPassword())">
                        <span class="material-symbols-outlined">
                            {{ showPassword() ? 'visibility_off' : 'visibility' }}
                        </span>
                    </button>
                </div>
                @if (form.controls.password.errors?.['passwordStrength'] && form.controls.password.touched) {
                    <p class="field-error">{{ form.controls.password.errors?.['passwordStrength'] }}</p>
                }

                <label class="field-label department-label">DEPARTMENT <span class="required">*</span></label>
                <juyo-option-toggle
                    class="department-toggle"
                    [class.invalid]="departmentError()"
                    [options]="departmentOptions"
                    [selectedOption]="selectedDepartmentOption()"
                    [openOnClick]="true"
                    [heightInPixels]="38"
                    [panelHeightInPixels]="160"
                    (optionClick)="onDepartmentSelect($event)"
                />
                @if (departmentError()) {
                    <p class="field-error">Please select your department.</p>
                }

                <juyo-button
                    [title]="'CREATE ACCOUNT'"
                    [isLoading]="isLoading()"
                    [useFullWidth]="true"
                    [buttonType]="ButtonType.PURPLE"
                    (clicked)="onSubmit()"
                >
                    Continue <span class="material-symbols-outlined">arrow_right_alt</span>
                </juyo-button>
            </form>
        } @else {
            <div class="heading-section">
                <h1 class="heading">Verify your email</h1>
                <p class="subtitle">We sent a 6-digit code to {{ form.value.email }}.</p>
            </div>

            @if (apiError()) {
                <div class="error-banner">{{ apiError() }}</div>
            }

            <form class="form" [formGroup]="verificationForm" novalidate>
                <label class="field-label" for="code"> VERIFICATION CODE <span class="required">*</span> </label>
                <input
                    type="text"
                    id="code"
                    class="field"
                    [class.invalid]="verificationForm.controls.code.invalid && verificationForm.controls.code.touched"
                    placeholder="000000"
                    formControlName="code"
                    maxlength="6"
                    autocomplete="one-time-code"
                />
                @if (verificationForm.controls.code.invalid && verificationForm.controls.code.touched) {
                    <p class="field-error">Please enter the 6-digit code from your email.</p>
                }

                <juyo-button
                    [title]="'VERIFY'"
                    [isLoading]="isLoading()"
                    [useFullWidth]="true"
                    [buttonType]="ButtonType.PURPLE"
                    (clicked)="onVerify()"
                >
                    Verify <span class="material-symbols-outlined">arrow_right_alt</span>
                </juyo-button>
            </form>
        }
    `,
})
export class SignUpComponent {
    public form: FormGroup<SignUpForm>;
    public verificationForm: FormGroup<VerificationForm>;
    public showPassword = signal(false);
    public isLoading = signal(false);
    public apiError = signal<string | null>(null);
    public step = signal<'details' | 'verification'>('details');

    public signedUp = output<void>();

    protected departmentOptions = DEPARTMENT_OPTIONS;
    protected selectedDepartmentOption = signal<Option>(DEPARTMENT_OPTIONS[0]);
    protected departmentError = signal(false);
    protected readonly ButtonType = ButtonType;
    protected readonly passwordMinLength = PASSWORD_MIN_LENGTH;

    private selectedDepartment = computed<Department | undefined>(() => {
        const key = this.selectedDepartmentOption().key;
        return key === NOT_SELECTABLE_KEY ? undefined : (key as Department);
    });
    private registrationService = inject(RegistrationService);
    private onboardingService = inject(OnboardingService);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private clientLoggingService = inject(ClientLoggingService);

    constructor() {
        this.form = new FormGroup<SignUpForm>({
            firstName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
            lastName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
            email: new FormControl('', [Validators.required, Validators.email, forbiddenDomainValidator]),
            password: new FormControl('', [Validators.required, passwordStrengthValidator]),
        });
        this.verificationForm = new FormGroup<VerificationForm>({
            code: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
        });
    }

    public onDepartmentSelect(option: Option): void {
        this.selectedDepartmentOption.set(option);
        this.departmentError.set(false);
    }

    public onSubmit(): void {
        this.form.markAllAsTouched();
        const departmentMissing = this.selectedDepartment() === undefined;
        this.departmentError.set(departmentMissing);
        if (this.form.invalid || departmentMissing) return;

        this.isLoading.set(true);
        this.apiError.set(null);

        const { firstName, lastName, email } = this.form.value as {
            firstName: string;
            lastName: string;
            email: string;
            password: string;
        };

        this.registrationService
            .userExists(email)
            .pipe(
                switchMap((exists) => {
                    if (exists) {
                        this.isLoading.set(false);
                        this.apiError.set('Your email address is already registered, try signing in instead.');
                        return EMPTY;
                    } else {
                        return this.onboardingService.createEmailVerification(firstName, lastName, email);
                    }
                }),
            )
            .subscribe({
                next: () => {
                    this.isLoading.set(false);
                    this.step.set('verification');
                },
                error: (err: unknown) => {
                    const correlationId = generateErrorId();
                    this.clientLoggingService.log(correlationId, err);
                    this.isLoading.set(false);
                    this.apiError.set('Something went wrong. Please try again.');
                },
            });
    }

    public onVerify(): void {
        this.verificationForm.markAllAsTouched();
        if (this.verificationForm.invalid) return;

        this.isLoading.set(true);
        this.apiError.set(null);

        const { firstName, lastName, email, password } = this.form.value as {
            firstName: string;
            lastName: string;
            email: string;
            password: string;
        };
        const department = this.selectedDepartment();
        const code = this.verificationForm.value.code as string;

        this.onboardingService.verifyEmail(email, code).subscribe({
            next: (result) => {
                this.isLoading.set(false);
                if (!result.success) {
                    this.apiError.set('Invalid or expired code. Please try again.');
                    return;
                }
                this.applicationStore.setSignupUser({
                    email,
                    password,
                    firstName,
                    lastName,
                    department: department as Department,
                });
                this.signedUp.emit();
            },
            error: (err: unknown) => {
                const correlationId = generateErrorId();
                this.clientLoggingService.log(correlationId, err);
                this.isLoading.set(false);
                this.apiError.set('Something went wrong. Please try again.');
            },
        });
    }
}

function forbiddenDomainValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (!value) return null;

    const atIndex = value.lastIndexOf('@');
    if (atIndex === -1) return null;

    const domain = value.slice(atIndex + 1).toLowerCase();
    return FORBIDDEN_EMAIL_DOMAINS.includes(domain) ? { forbiddenDomain: true } : null;
}

interface SignUpForm {
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    email: FormControl<string | null>;
    password: FormControl<string | null>;
}

interface VerificationForm {
    code: FormControl<string | null>;
}
